/**
 * subjectPalette.ts — single source of truth for subject color identity.
 *
 * May 2026 launch plan §3.5 — refined palette so colors aid recognition
 * (each subject has a stable hue) without going loud or premium-feeling.
 * Chemistry moved off purple (was reading as "premium / paid") to teal,
 * which sits closer to the lab-glass aesthetic and balances the row.
 *
 * Used by: SubjectSelectPage, ProfilePage, AdminQuestionsPage,
 * AdminDashboardPage, anywhere subject identity needs a colour cue.
 *
 * SUBJECT_HUE      — base palette name (Tailwind colour family)
 * SUBJECT_BADGE    — small inline label used on rows / chips
 * SUBJECT_EMOJI    — single emoji per subject (kept in sync across pages)
 */

export type SubjectHue = 'blue' | 'green' | 'amber' | 'teal' | 'rose' | 'slate'

export const SUBJECT_HUE: Record<string, SubjectHue> = {
  MAT: 'blue',
  BIO: 'green',
  FIS: 'amber',
  QUI: 'teal',
  POR: 'rose',
  HIS: 'slate',
}

export const SUBJECT_EMOJI: Record<string, string> = {
  MAT: '📐',
  BIO: '🧬',
  FIS: '⚡',
  QUI: '🧪',
  POR: '📝',
  HIS: '📜',
}

/** Shared badge/chip class string — light bg + matching dark text. */
export const SUBJECT_BADGE: Record<string, string> = {
  MAT: 'bg-blue-100 text-blue-700',
  BIO: 'bg-green-100 text-green-700',
  FIS: 'bg-amber-100 text-amber-800',
  QUI: 'bg-teal-100 text-teal-700',
  POR: 'bg-rose-100 text-rose-700',
  HIS: 'bg-slate-100 text-slate-700',
}

/** Full per-hue Tailwind class set used by the SubjectSelectPage cards.
 *  Kept in this module so any page that wants the same look can import it. */
export const SUBJECT_HUE_CLS: Record<SubjectHue, {
  hdr: string
  badge: string
  btn: string
  ring: string
  bar: string
  /** Outlined pill: saturated ring plus matching text on white. For inline
   *  actions that sit among the filled topic pills and must read as
   *  tappable without becoming a second primary CTA. Uses `ring` rather
   *  than `border` so the outline adds no height and the pill stays
   *  aligned with the topic pills beside it. */
  outlined: string
}> = {
  blue:  { hdr: 'bg-blue-50',  badge: 'bg-blue-100 text-blue-700',   btn: 'bg-blue-600 hover:bg-blue-700',   ring: 'ring-blue-400',  bar: 'bg-blue-500',  outlined: 'ring-blue-500 text-blue-700 hover:bg-blue-50'    },
  green: { hdr: 'bg-green-50', badge: 'bg-green-100 text-green-700', btn: 'bg-green-600 hover:bg-green-700', ring: 'ring-green-400', bar: 'bg-green-500', outlined: 'ring-green-500 text-green-700 hover:bg-green-50'  },
  amber: { hdr: 'bg-amber-50', badge: 'bg-amber-100 text-amber-800', btn: 'bg-amber-500 hover:bg-amber-600', ring: 'ring-amber-400', bar: 'bg-amber-500', outlined: 'ring-amber-600 text-amber-800 hover:bg-amber-50'  },
  teal:  { hdr: 'bg-teal-50',  badge: 'bg-teal-100 text-teal-700',   btn: 'bg-teal-600 hover:bg-teal-700',   ring: 'ring-teal-400',  bar: 'bg-teal-500',  outlined: 'ring-teal-500 text-teal-700 hover:bg-teal-50'    },
  rose:  { hdr: 'bg-rose-50',  badge: 'bg-rose-100 text-rose-700',   btn: 'bg-rose-600 hover:bg-rose-700',   ring: 'ring-rose-400',  bar: 'bg-rose-500',  outlined: 'ring-rose-500 text-rose-700 hover:bg-rose-50'    },
  slate: { hdr: 'bg-slate-50', badge: 'bg-slate-100 text-slate-700', btn: 'bg-slate-600 hover:bg-slate-700', ring: 'ring-slate-400', bar: 'bg-slate-500', outlined: 'ring-slate-500 text-slate-700 hover:bg-slate-50' },
}
